/**
 * 登录 Modal(V3 — 支持验证码 / 密码双模式 + 注册设密码)
 *
 * 模式:
 *   - 验证码登录(默认):手机号 → 验证码 → 登录/注册
 *   - 密码登录:手机号 + 密码 → 登录
 *   - 注册时可设密码(可选)
 *   - 已设密码:登录后弹"改密码"入口
 */
import { useState, useEffect } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import { useNavigate, useLocation } from 'react-router-dom';
import {
  Phone, ShieldCheck, X, Loader2, LogIn, Sparkles, Lock, Eye, EyeOff,
  MessageSquare, KeyRound, HelpCircle, AlertCircle
} from 'lucide-react';
import toast from 'react-hot-toast';
import clsx from 'clsx';
import { useUser } from '../stores/user.js';
import { api } from '../lib/api.js';
import ForgotPasswordModal from './ForgotPasswordModal.jsx';

const STEPS = {
  PHONE: 'phone',
  CODE: 'code',
  SET_PASSWORD: 'set_password',  // 注册后引导设密码
};

export default function LoginModal({ open, onClose, onSuccess }) {
  const navigate = useNavigate();
  const location = useLocation();
  const { sendCode, login, loading, setPassword: setUserPassword } = useUser();
  const [mode, setMode] = useState('code');  // 'code' | 'password'
  const [step, setStep] = useState(STEPS.PHONE);
  const [phone, setPhone] = useState('');
  const [code, setCode] = useState('');
  const [password, setPassword] = useState('');
  const [showPwd, setShowPwd] = useState(false);
  const [newPassword, setNewPassword] = useState('');
  const [verifyCode, setVerifyCode] = useState('');
  const [countdown, setCountdown] = useState(0);
  const [devCode, setDevCode] = useState('');
  const [devMode, setDevMode] = useState(false);  // SMS 开发模式(签名申请期间)
  const [queryLoading, setQueryLoading] = useState(false);
  const [showForgot, setShowForgot] = useState(false);

  // 登录成功后:处理 pending action(跳转到 /profile 让用户走真支付)
  const handlePostLogin = () => {
    try {
      const pending = sessionStorage.getItem('fucai3d_pending_action');
      if (pending) {
        const action = JSON.parse(pending);
        // 不删!留着给 ProfilePage 继续用
        if (action.type === 'upgrade') {
          // 跳回 /profile,ProfilePage 会自动打开 PaymentModal
          const from = sessionStorage.getItem('fucai3d_redirect_from') || '/profile';
          if (location.pathname !== from) {
            navigate(from);
          }
        }
      }
    } catch {}

    // 2. 跳回原页面(如果存在)
    try {
      const from = sessionStorage.getItem('fucai3d_redirect_from');
      if (from && from !== location.pathname) {
        sessionStorage.removeItem('fucai3d_redirect_from');
        setTimeout(() => navigate(from), 100);
        return true;
      }
    } catch {}
    return false;
  };

  useEffect(() => {
    if (countdown <= 0) return;
    const t = setTimeout(() => setCountdown(c => c - 1), 1000);
    return () => clearTimeout(t);
  }, [countdown]);

  useEffect(() => {
    if (!open) {
      setStep(STEPS.PHONE);
      setPhone('');
      setCode('');
      setPassword('');
      setNewPassword('');
      setVerifyCode('');
      setCountdown(0);
      setDevCode('');
      setDevMode(false);
      setMode('code');
    }
  }, [open]);

  const phoneValid = /^1[3-9]\d{9}$/.test(phone);

  const handleSendCode = async () => {
    if (!phoneValid) {
      toast.error('请输入正确的手机号');
      return;
    }
    const res = await sendCode(phone);
    if (res.ok) {
      if (res.dev_mode) {
        toast('验证码已生成(开发模式 — 请联系客服获取)', { icon: '⏳', duration: 5000 });
      } else {
        toast.success('验证码已发送');
      }
      setCountdown(60);
      if (res._devCode) setDevCode(res._devCode);
      setDevMode(!!res.dev_mode);
      setStep(STEPS.CODE);
    } else {
      toast.error('发送失败,请重试');
    }
  };

  // 查询我的验证码(开发模式用)
  const handleQueryCode = async () => {
    if (!phoneValid) {
      toast.error('请输入正确的手机号');
      return;
    }
    setQueryLoading(true);
    try {
      const res = await api.queryCode(phone);
      if (res.ok) {
        setCode(res.code);
        toast.success(`已查到验证码: ${res.code} (剩 ${res.remain_seconds} 秒)`, { duration: 8000 });
      } else {
        toast.error(res.error || '未找到验证码,请先获取');
      }
    } catch (e) {
      toast.error('查询失败: ' + e.message);
    } finally {
      setQueryLoading(false);
    }
  };

  // 联系客服(开发模式用)
  const handleContactCS = () => {
    // 弹个 toast + 显示客服微信号
    toast('请加客服微信获取验证码:beemafkj_cs (待你配置)', { icon: '💬', duration: 10000 });
  };

  const handleLoginByCode = async () => {
    if (code.length !== 6) {
      toast.error('请输入 6 位验证码');
      return;
    }
    const refCode = localStorage.getItem('fucai3d_referral_code') || '';
    const res = await login(phone, code, refCode);
    if (res.ok) {
      toast.success(res.isNew ? '注册成功!' : '登录成功!');
      if (refCode) localStorage.removeItem('fucai3d_referral_code');
      // 新用户引导设密码
      if (res.isNew) {
        setStep(STEPS.SET_PASSWORD);
      } else {
        const didRedirect = handlePostLogin();
        onSuccess && onSuccess();
        if (!didRedirect) onClose();
      }
    } else {
      toast.error(res.msg || '验证失败');
    }
  };

  const handleLoginByPassword = async () => {
    if (!phoneValid) {
      toast.error('请输入正确的手机号');
      return;
    }
    if (password.length < 6) {
      toast.error('密码至少 6 位');
      return;
    }
    try {
      const res = await api.loginPassword(phone, password);
      if (res.ok) {
        toast.success('登录成功!');
        const didRedirect = handlePostLogin();
        onSuccess && onSuccess();
        if (!didRedirect) onClose();
      } else {
        toast.error(res.error || '登录失败');
      }
    } catch (e) {
      toast.error(e.message || '登录失败');
    }
  };

  const handleSetPassword = async () => {
    if (newPassword.length < 6) {
      toast.error('密码至少 6 位');
      return;
    }
    if (newPassword.length > 64) {
      toast.error('密码不能超过 64 位');
      return;
    }
    if (/^\d+$/.test(newPassword)) {
      toast.error('密码不能全是数字');
      return;
    }
    if (verifyCode.length !== 6) {
      toast.error('请输入 6 位验证码');
      return;
    }
    try {
      const res = await api.setPassword(newPassword, verifyCode);
      if (res.ok) {
        toast.success('密码已设置!');
        onSuccess && onSuccess();
        onClose();
      } else {
        toast.error(res.error || '设置失败');
      }
    } catch (e) {
      toast.error(e.message || '设置失败');
    }
  };

  const handleSkipSetPassword = () => {
    onSuccess && onSuccess();
    onClose();
  };

  if (!open) return null;

  return (
    <AnimatePresence>
      <motion.div
        initial={{ opacity: 0 }}
        animate={{ opacity: 1 }}
        exit={{ opacity: 0 }}
        className="fixed inset-0 z-50 bg-black/60 backdrop-blur-sm flex items-center justify-center p-4"
        onClick={onClose}
      >
        <motion.div
          initial={{ y: 30, opacity: 0, scale: 0.95 }}
          animate={{ y: 0, opacity: 1, scale: 1 }}
          exit={{ y: 30, opacity: 0 }}
          transition={{ type: 'spring', damping: 25, stiffness: 300 }}
          onClick={e => e.stopPropagation()}
          className="w-full sm:max-w-md glass-strong rounded-2xl p-6 sm:p-8 relative"
        >
          <button
            onClick={onClose}
            className="absolute top-4 right-4 text-ink-3 hover:text-ink-0 transition"
          >
            <X size={20} />
          </button>

          <div className="text-center mb-5">
            <div className="inline-flex items-center justify-center w-14 h-14 rounded-2xl gradient-bg mb-3">
              <LogIn size={26} className="text-bg-base" />
            </div>
            <h2 className="text-xl font-bold">
              {step === STEPS.SET_PASSWORD
                ? '🔐 设置登录密码(可选)'
                : step === STEPS.CODE
                  ? '输入验证码'
                  : mode === 'password'
                    ? '密码登录'
                    : '手机号登录/注册'}
            </h2>
            <p className="text-xs text-ink-3 mt-1">
              {step === STEPS.SET_PASSWORD
                ? '下次可以密码登录,不用每次输验证码'
                : step === STEPS.CODE
                  ? `验证码已发送至 ${phone.slice(0, 3)}****${phone.slice(-4)}`
                  : mode === 'password'
                    ? '用密码登录(已设过密码的用户)'
                    : '未注册的手机号将自动创建账号'}
            </p>
          </div>

          <AnimatePresence mode="wait">
            {/* Step 1: 手机号 */}
            {step === STEPS.PHONE && (
              <motion.div
                key="phone"
                initial={{ opacity: 0, x: -10 }}
                animate={{ opacity: 1, x: 0 }}
                exit={{ opacity: 0, x: 10 }}
                className="space-y-4"
              >
                {/* 模式切换 */}
                <div className="grid grid-cols-2 gap-1 bg-white/5 p-1 rounded-lg">
                  <button
                    onClick={() => setMode('code')}
                    className={clsx(
                      'py-1.5 rounded-md text-xs font-semibold transition flex items-center justify-center gap-1.5',
                      mode === 'code' ? 'bg-brand-purple/30 text-brand-purple' : 'text-ink-2'
                    )}
                  >
                    <MessageSquare size={12} /> 验证码登录
                  </button>
                  <button
                    onClick={() => setMode('password')}
                    className={clsx(
                      'py-1.5 rounded-md text-xs font-semibold transition flex items-center justify-center gap-1.5',
                      mode === 'password' ? 'bg-brand-purple/30 text-brand-purple' : 'text-ink-2'
                    )}
                  >
                    <KeyRound size={12} /> 密码登录
                  </button>
                </div>

                <div>
                  <label className="text-xs text-ink-3 mb-1.5 block">手机号</label>
                  <div className="flex items-center gap-2 bg-bg-card border border-white/10 rounded-lg px-3 py-2.5 focus-within:border-brand-purple/40 transition">
                    <Phone size={16} className="text-ink-3" />
                    <span className="text-ink-2 text-sm">+86</span>
                    <input
                      type="tel"
                      value={phone}
                      onChange={e => setPhone(e.target.value.replace(/\D/g, '').slice(0, 11))}
                      placeholder="13800000000"
                      maxLength={11}
                      autoFocus
                      className="flex-1 bg-transparent outline-none text-base"
                    />
                  </div>
                </div>

                {mode === 'code' ? (
                  <button
                    onClick={handleSendCode}
                    disabled={!phoneValid || loading}
                    className={clsx(
                      'w-full py-3 rounded-lg font-semibold text-sm transition-all flex items-center justify-center gap-2',
                      phoneValid && !loading
                        ? 'btn-primary'
                        : 'bg-white/5 text-ink-3 cursor-not-allowed'
                    )}
                  >
                    {loading ? <Loader2 size={16} className="animate-spin" /> : <Sparkles size={16} />}
                    获取验证码
                  </button>
                ) : (
                  <>
                    <div>
                      <label className="text-xs text-ink-3 mb-1.5 block">密码</label>
                      <div className="flex items-center gap-2 bg-bg-card border border-white/10 rounded-lg px-3 py-2.5 focus-within:border-brand-purple/40 transition">
                        <Lock size={16} className="text-ink-3" />
                        <input
                          type={showPwd ? 'text' : 'password'}
                          value={password}
                          onChange={e => setPassword(e.target.value)}
                          placeholder="6-64 位,不能全是数字"
                          className="flex-1 bg-transparent outline-none text-base"
                        />
                        <button onClick={() => setShowPwd(!showPwd)} className="text-ink-3">
                          {showPwd ? <EyeOff size={16} /> : <Eye size={16} />}
                        </button>
                      </div>
                    </div>
                    <button
                      onClick={handleLoginByPassword}
                      disabled={!phoneValid || password.length < 6 || loading}
                      className={clsx(
                        'w-full py-3 rounded-lg font-semibold text-sm transition-all flex items-center justify-center gap-2',
                        phoneValid && password.length >= 6 && !loading
                          ? 'btn-primary'
                          : 'bg-white/5 text-ink-3 cursor-not-allowed'
                      )}
                    >
                      {loading ? <Loader2 size={16} className="animate-spin" /> : <KeyRound size={16} />}
                      密码登录
                    </button>
                    <button
                      type="button"
                      onClick={() => {
                        setShowForgot(true);
                        setPhone(phone);  // 把手机号传过去
                      }}
                      className="w-full text-center text-xs text-brand-cyan hover:underline flex items-center justify-center gap-1"
                    >
                      <HelpCircle size={12} />
                      忘记密码?
                    </button>
                  </>
                )}
              </motion.div>
            )}

            {/* Step 2: 验证码 */}
            {step === STEPS.CODE && (
              <motion.div
                key="code"
                initial={{ opacity: 0, x: -10 }}
                animate={{ opacity: 1, x: 0 }}
                exit={{ opacity: 0, x: 10 }}
                className="space-y-4"
              >
                {/* 开发模式横幅 — 显示在输入框上方 */}
                {devMode && (
                  <div className="rounded-xl p-3 border border-amber-500/30 bg-amber-500/10 text-xs text-amber-200/90 flex items-start gap-2">
                    <AlertCircle className="w-4 h-4 text-amber-400 flex-shrink-0 mt-0.5" />
                    <div className="flex-1">
                      <div className="font-medium mb-0.5">验证码由客服同步发送</div>
                      <div className="text-amber-200/70 mb-2">
                        推广期间(7-10 天),您的验证码会通过客服微信/钉钉发送。获取后 5 分钟内有效。
                      </div>
                      {/* 快捷操作 */}
                      <div className="flex gap-2">
                        <button
                          onClick={handleQueryCode}
                          disabled={queryLoading}
                          className="px-2 py-1 rounded bg-amber-500/20 hover:bg-amber-500/30 text-amber-100 text-[10px] font-medium transition flex items-center gap-1 disabled:opacity-50"
                        >
                          {queryLoading ? <Loader2 className="w-3 h-3 animate-spin" /> : <Search className="w-3 h-3" />}
                          查询我的验证码
                        </button>
                        <a
                          href="javascript:void(0)"
                          onClick={handleContactCS}
                          className="px-2 py-1 rounded bg-amber-500/20 hover:bg-amber-500/30 text-amber-100 text-[10px] font-medium transition flex items-center gap-1"
                        >
                          <MessageSquare className="w-3 h-3" />
                          联系客服
                        </a>
                      </div>
                    </div>
                  </div>
                )}

                <div>
                  <label className="text-xs text-ink-3 mb-1.5 block">6 位验证码</label>
                  <input
                    type="text"
                    inputMode="numeric"
                    maxLength={6}
                    value={code}
                    onChange={e => setCode(e.target.value.replace(/\D/g, ''))}
                    placeholder="000000"
                    className="w-full bg-bg-card border border-white/10 rounded-lg px-4 py-3 text-center text-2xl font-mono tracking-[0.5em] outline-none focus:border-brand-purple/40 transition"
                    autoFocus
                  />
                </div>

                {devCode && (
                  <div className="text-[10px] text-center text-ink-3 bg-brand-gold/5 border border-brand-gold/20 rounded p-2">
                    🛠 开发模式验证码:<span className="font-mono text-brand-gold ml-1">{devCode}</span>
                  </div>
                )}

                <button
                  onClick={handleLoginByCode}
                  disabled={code.length !== 6 || loading}
                  className={clsx(
                    'w-full py-3 rounded-lg font-semibold text-sm transition-all flex items-center justify-center gap-2',
                    code.length === 6 && !loading
                      ? 'btn-primary'
                      : 'bg-white/5 text-ink-3 cursor-not-allowed'
                  )}
                >
                  {loading ? <Loader2 size={16} className="animate-spin" /> : <ShieldCheck size={16} />}
                  登录 / 注册
                </button>

                <div className="flex justify-between text-xs">
                  <button
                    onClick={() => {
                      setStep(STEPS.PHONE);
                      setCode('');
                    }}
                    className="text-ink-3 hover:text-ink-0"
                  >
                    ← 换手机号
                  </button>
                  <button
                    onClick={handleSendCode}
                    disabled={countdown > 0}
                    className={clsx(countdown > 0 ? 'text-ink-3' : 'text-brand-cyan')}
                  >
                    {countdown > 0 ? `${countdown}秒后重发` : '重发验证码'}
                  </button>
                </div>
              </motion.div>
            )}

            {/* Step 3: 设置密码(新用户) */}
            {step === STEPS.SET_PASSWORD && (
              <motion.div
                key="setpwd"
                initial={{ opacity: 0, x: -10 }}
                animate={{ opacity: 1, x: 0 }}
                exit={{ opacity: 0, x: 10 }}
                className="space-y-4"
              >
                <div className="rounded-lg bg-brand-gold/10 border border-brand-gold/30 p-3 text-xs text-ink-1">
                  💡 设置密码后,下次可直接用密码登录(不用每次输验证码)
                </div>

                <div>
                  <label className="text-xs text-ink-3 mb-1.5 block">设置密码(6-64 位,不能全是数字)</label>
                  <div className="flex items-center gap-2 bg-bg-card border border-white/10 rounded-lg px-3 py-2.5 focus-within:border-brand-purple/40 transition">
                    <Lock size={16} className="text-ink-3" />
                    <input
                      type={showPwd ? 'text' : 'password'}
                      value={newPassword}
                      onChange={e => setNewPassword(e.target.value)}
                      placeholder="建议字母+数字组合"
                      maxLength={64}
                      autoFocus
                      className="flex-1 bg-transparent outline-none"
                    />
                    <button onClick={() => setShowPwd(!showPwd)} className="text-ink-3">
                      {showPwd ? <EyeOff size={16} /> : <Eye size={16} />}
                    </button>
                  </div>
                </div>

                <div>
                  <label className="text-xs text-ink-3 mb-1.5 block">手机验证码(二次验证)</label>
                  <input
                    type="text"
                    inputMode="numeric"
                    maxLength={6}
                    value={verifyCode}
                    onChange={e => setVerifyCode(e.target.value.replace(/\D/g, ''))}
                    placeholder="输入 123456"
                    className="w-full bg-bg-card border border-white/10 rounded-lg px-4 py-2.5 text-center text-lg font-mono tracking-[0.3em] outline-none focus:border-brand-purple/40"
                  />
                  <div className="text-[10px] text-ink-3 mt-1 text-center">
                    开发模式验证码:123456
                  </div>
                </div>

                <div className="grid grid-cols-2 gap-2">
                  <button
                    onClick={handleSkipSetPassword}
                    className="btn-secondary"
                  >
                    稍后设置
                  </button>
                  <button
                    onClick={handleSetPassword}
                    disabled={newPassword.length < 6 || verifyCode.length !== 6}
                    className={clsx(
                      'py-3 rounded-lg font-semibold text-sm transition-all flex items-center justify-center gap-2',
                      newPassword.length >= 6 && verifyCode.length === 6
                        ? 'btn-primary'
                        : 'bg-white/5 text-ink-3 cursor-not-allowed'
                    )}
                  >
                    <Lock size={14} />
                    设置密码
                  </button>
                </div>
              </motion.div>
            )}
          </AnimatePresence>

          {/* 底部协议 */}
          {step === STEPS.PHONE && (
            <p className="text-[10px] text-ink-3 text-center mt-5">
              登录即同意 <a href="#" className="text-brand-cyan">《用户协议》</a> 和
              <a href="#" className="text-brand-cyan ml-1">《隐私政策》</a>
            </p>
          )}
        </motion.div>
      </motion.div>

      {/* 找回密码弹窗 */}
      <ForgotPasswordModal
        open={showForgot}
        onClose={() => setShowForgot(false)}
        onSwitchToLogin={(phoneFromForgot) => {
          // 重设成功后,自动切回密码登录 tab,带手机号
          if (phoneFromForgot) {
            setPhone(phoneFromForgot);
            setMode('password');
          }
        }}
      />
    </AnimatePresence>
  );
}
